import { useState, useEffect, useMemo, useRef } from 'react';
import { Clock, Users, Timer, ChevronRight, CalendarClock, MapPin, AlertCircle, Star, Phone, Utensils } from 'lucide-react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';

interface Reservation {
  id: string;
  guestName: string;
  partySize: number;
  time: string;
  status: string;
  specialRequests?: string;
  notes?: string;
  tableNumber?: string | number;
  tableName?: string;
  tableId?: string;
  assignedTableId?: string;
  duration?: number; // in minutes
  phone?: string;
  email?: string;
  checkInTime?: string;
  guest?: {
    isVip?: boolean;
    allergies?: string[];
    tags?: string[];
    visitCount?: number;
  };
}

interface HourlyForecast {
  hour: number;
  totalExpected: number;
  reservationCovers: number;
  walkInEstimate: number;
}

interface ShiftTimelineBarProps {
  shiftName: string;
  shiftStart: string;        // ISO datetime string
  shiftEnd: string;          // ISO datetime string
  shiftStatus: 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
  reservations: Reservation[];
  hourlyForecast?: HourlyForecast[];
  peakHour?: number;
  waitlistCount: number;
  onHourSelect?: (hour: number | null) => void; // Callback for simulating time
}

function formatHour(hour: number): string {
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}${period}`;
}

function formatTime(time: string): string {
  const [hours, mins] = time.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHour = hours % 12 || 12;
  return `${displayHour}:${mins.toString().padStart(2, '0')} ${period}`;
}

function formatTimeFromDate(date: Date): string {
  const hours = date.getUTCHours();
  const minutes = date.getUTCMinutes();
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHour = hours % 12 || 12;
  return `${displayHour}:${minutes.toString().padStart(2, '0')} ${period}`;
}

type BusyLevel = 'low' | 'moderate' | 'busy' | 'peak';

export default function ShiftTimelineBar({
  shiftName,
  shiftStart,
  shiftEnd,
  shiftStatus,
  reservations,
  hourlyForecast = [],
  peakHour,
  waitlistCount,
  onHourSelect,
}: ShiftTimelineBarProps) {
  const [now, setNow] = useState(new Date());
  const [hoveredHour, setHoveredHour] = useState<number | null>(null);
  const [popoverPosition, setPopoverPosition] = useState<{ x: number; y: number; showAbove?: boolean } | null>(null);
  const timelineRef = useRef<HTMLDivElement>(null);

  // Update current time every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(new Date());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Parse shift boundaries from ISO strings
  const shiftStartDate = useMemo(() => new Date(shiftStart), [shiftStart]);
  const shiftEndDate = useMemo(() => new Date(shiftEnd), [shiftEnd]);

  const shiftStartMinutes = shiftStartDate.getUTCHours() * 60 + shiftStartDate.getUTCMinutes();
  const shiftEndMinutes = shiftEndDate.getUTCHours() * 60 + shiftEndDate.getUTCMinutes();
  const shiftDuration = shiftEndMinutes - shiftStartMinutes;
  const currentMinutes = now.getHours() * 60 + now.getMinutes();

  // Calculate hours to display
  const startHour = Math.floor(shiftStartMinutes / 60);
  const endHour = Math.ceil(shiftEndMinutes / 60);
  const hours = Array.from({ length: endHour - startHour }, (_, i) => startHour + i);

  // Calculate current position as percentage
  const currentPosition = useMemo(() => {
    if (currentMinutes < shiftStartMinutes) return 0;
    if (currentMinutes > shiftEndMinutes) return 100;
    return ((currentMinutes - shiftStartMinutes) / shiftDuration) * 100;
  }, [currentMinutes, shiftStartMinutes, shiftEndMinutes, shiftDuration]);

  // Check shift timing status
  const isWithinShift = currentMinutes >= shiftStartMinutes && currentMinutes <= shiftEndMinutes;
  const isBeforeShift = currentMinutes < shiftStartMinutes;
  const isAfterShift = currentMinutes > shiftEndMinutes;

  // Calculate time remaining or until shift starts
  const minutesRemaining = Math.max(0, shiftEndMinutes - currentMinutes);
  const hoursRemaining = Math.floor(minutesRemaining / 60);
  const minsRemaining = minutesRemaining % 60;

  const minutesUntilStart = Math.max(0, shiftStartMinutes - currentMinutes);
  const hoursUntilStart = Math.floor(minutesUntilStart / 60);
  const minsUntilStart = minutesUntilStart % 60;

  // Group reservations by hour for display
  const reservationsByHour = useMemo(() => {
    const byHour: Record<number, Reservation[]> = {};
    for (const res of reservations) {
      if (!res.time) continue;
      const [hours] = res.time.split(':').map(Number);
      // Only include reservations within the shift time window
      if (hours >= startHour && hours < endHour) {
        if (!byHour[hours]) byHour[hours] = [];
        byHour[hours].push(res);
      }
    }
    // Sort reservations within each hour by time
    for (const hour of Object.keys(byHour)) {
      byHour[parseInt(hour)].sort((a, b) => a.time.localeCompare(b.time));
    }
    return byHour;
  }, [reservations, startHour, endHour]);

  // Check if we have any meaningful data
  const hasAnyReservations = Object.values(reservationsByHour).some(arr => arr.length > 0);
  const hasForecastData = hourlyForecast.length > 0;
  const hasData = hasAnyReservations || hasForecastData;

  // Find actual peak hour based on data (not just passed-in value)
  const actualPeakHour = useMemo(() => {
    if (!hasData) return null;

    // If we have forecast data, use that to find peak
    if (hasForecastData) {
      const maxHour = hourlyForecast.reduce((max, curr) =>
        curr.totalExpected > (max?.totalExpected || 0) ? curr : max
      , hourlyForecast[0]);
      // Only consider it peak if there's meaningful volume
      if (maxHour && maxHour.totalExpected >= 10) {
        return maxHour.hour;
      }
    }

    // Fallback to reservation counts
    let maxRes = 0;
    let maxHour: number | null = null;
    for (const [hour, res] of Object.entries(reservationsByHour)) {
      if (res.length > maxRes) {
        maxRes = res.length;
        maxHour = parseInt(hour);
      }
    }
    // Only consider peak if there are at least 4 reservations in that hour
    return maxRes >= 4 ? maxHour : null;
  }, [hasData, hasForecastData, hourlyForecast, reservationsByHour]);

  // Get busy level for an hour
  const getBusyLevel = (hour: number): BusyLevel => {
    // If no data at all, everything is neutral/low
    if (!hasData) return 'low';

    // Check if this is the peak hour
    if (actualPeakHour !== null && hour === actualPeakHour) return 'peak';

    const hourData = hourlyForecast.find(f => f.hour === hour);
    const resCount = reservationsByHour[hour]?.length || 0;

    // If we have forecast data, use that
    if (hourData && hasForecastData) {
      const maxCovers = Math.max(...hourlyForecast.map(f => f.totalExpected), 1);
      const ratio = hourData.totalExpected / maxCovers;

      if (ratio > 0.8) return 'busy';
      if (ratio > 0.5) return 'moderate';
      return 'low';
    }

    // Fallback to reservation count
    if (resCount >= 6) return 'busy';
    if (resCount >= 3) return 'moderate';
    return 'low';
  };

  // Get style for busy level
  const getBusyLevelStyle = (level: BusyLevel) => {
    switch (level) {
      case 'peak':
        return 'bg-danger-100 border-danger-200';
      case 'busy':
        return 'bg-warning-100 border-warning-200';
      case 'moderate':
        return 'bg-amber-50 border-amber-200';
      case 'low':
      default:
        return 'bg-success-50 border-success-200';
    }
  };

  // Count upcoming reservations within shift
  const upcomingReservations = reservations.filter(r => {
    if (!r.time) return false;
    const [hours, mins] = r.time.split(':').map(Number);
    const resMinutes = hours * 60 + mins;
    return resMinutes >= currentMinutes &&
           resMinutes >= shiftStartMinutes &&
           resMinutes <= shiftEndMinutes &&
           ['pending', 'confirmed'].includes(r.status);
  });

  // Total expected covers for rest of shift
  const expectedCoversRemaining = hourlyForecast
    .filter(f => f.hour * 60 >= currentMinutes && f.hour >= startHour && f.hour < endHour)
    .reduce((sum, f) => sum + f.totalExpected, 0);

  // Get shift status styling
  const getShiftStatusBadge = () => {
    switch (shiftStatus) {
      case 'in_progress':
        return <span className="px-2 py-0.5 text-xs font-medium bg-success-100 text-success-700 rounded-full">In Progress</span>;
      case 'completed':
        return <span className="px-2 py-0.5 text-xs font-medium bg-slate-100 text-slate-600 rounded-full">Completed</span>;
      case 'cancelled':
        return <span className="px-2 py-0.5 text-xs font-medium bg-danger-100 text-danger-700 rounded-full">Cancelled</span>;
      default:
        return <span className="px-2 py-0.5 text-xs font-medium bg-primary-100 text-primary-700 rounded-full">Scheduled</span>;
    }
  };

  // Handle hour hover - use fixed viewport positioning for portal
  const handleHourHover = (hour: number, event: React.MouseEvent<HTMLDivElement>) => {
    setHoveredHour(hour);
    onHourSelect?.(hour);

    // Calculate popover position in viewport coordinates (for portal)
    const rect = event.currentTarget.getBoundingClientRect();
    const popoverWidth = 280;
    const popoverHeight = 300; // estimated max height

    // Center horizontally, but keep within viewport
    let x = rect.left + rect.width / 2;
    const leftEdge = x - popoverWidth / 2;
    const rightEdge = x + popoverWidth / 2;

    if (leftEdge < 8) {
      x = popoverWidth / 2 + 8;
    } else if (rightEdge > window.innerWidth - 8) {
      x = window.innerWidth - popoverWidth / 2 - 8;
    }

    // Position below the element, but flip above if not enough space
    const spaceBelow = window.innerHeight - rect.bottom;
    const showAbove = spaceBelow < popoverHeight && rect.top > popoverHeight;

    const y = showAbove ? rect.top - 8 : rect.bottom + 8;

    setPopoverPosition({ x, y, showAbove });
  };

  const handleMouseLeave = () => {
    setHoveredHour(null);
    setPopoverPosition(null);
    onHourSelect?.(null);
  };

  // Get total covers for an hour
  const getHourCovers = (hour: number) => {
    const resList = reservationsByHour[hour] || [];
    return resList.reduce((sum, r) => sum + r.partySize, 0);
  };

  return (
    <div className="card overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CalendarClock className="w-4 h-4 text-primary-500" />
            <div>
              <h3 className="text-sm font-semibold text-slate-900">{shiftName}</h3>
              <span className="text-xs text-slate-500">
                {formatTimeFromDate(shiftStartDate)} - {formatTimeFromDate(shiftEndDate)}
              </span>
            </div>
          </div>
          {getShiftStatusBadge()}
        </div>
      </div>

      {/* Timeline */}
      <div className="px-4 py-4" ref={timelineRef}>
        {/* Time labels - positioned at hour boundaries */}
        <div className="relative h-5 mb-1">
          {hours.map((hour, idx) => {
            // Calculate position as percentage of shift duration
            const hourMinutes = hour * 60;
            const position = ((hourMinutes - shiftStartMinutes) / shiftDuration) * 100;
            const isFirst = idx === 0;

            return (
              <span
                key={hour}
                className={clsx(
                  'absolute text-xs text-slate-500',
                  !isFirst && '-translate-x-1/2'
                )}
                style={{ left: `${position}%` }}
              >
                {formatHour(hour)}
              </span>
            );
          })}
          {/* End time label */}
          <span
            className="absolute text-xs text-slate-500 right-0"
          >
            {formatHour(endHour)}
          </span>
        </div>

        {/* Timeline bar */}
        <div className="relative h-14 rounded-lg overflow-visible border border-slate-200 bg-slate-100">
          {/* Hour segments with busy levels */}
          <div className="absolute inset-0 flex">
            {hours.map((hour) => {
              const level = getBusyLevel(hour);
              const resInHour = reservationsByHour[hour] || [];
              const isHovered = hoveredHour === hour;

              return (
                <div
                  key={hour}
                  className={clsx(
                    'relative flex-1 border-r border-slate-200 last:border-r-0 transition-all cursor-pointer',
                    getBusyLevelStyle(level),
                    isHovered && 'ring-2 ring-primary-400 ring-inset z-10'
                  )}
                  onMouseEnter={(e) => handleHourHover(hour, e)}
                  onMouseLeave={handleMouseLeave}
                >
                  {/* Reservation dots */}
                  {resInHour.length > 0 && (
                    <div className="absolute inset-x-1 top-1 flex flex-wrap gap-0.5 justify-center">
                      {resInHour.slice(0, 6).map((res) => (
                        <div
                          key={res.id}
                          className={clsx(
                            'w-2 h-2 rounded-full',
                            res.guest?.isVip ? 'bg-purple-500 ring-1 ring-purple-300' :
                            res.status === 'confirmed' ? 'bg-primary-500' : 'bg-slate-400'
                          )}
                        />
                      ))}
                      {resInHour.length > 6 && (
                        <span className="text-[10px] text-slate-600 font-medium">
                          +{resInHour.length - 6}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Peak hour indicator - only show if we have data to support it */}
                  {actualPeakHour !== null && hour === actualPeakHour && (
                    <div className="absolute bottom-1 inset-x-0 text-center">
                      <span className="text-[10px] font-semibold text-danger-600 uppercase tracking-wide">
                        Peak
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Hover Popover - rendered via portal for proper z-index */}
          {hoveredHour !== null && popoverPosition && typeof document !== 'undefined' && createPortal(
            <div
              className="fixed z-[9999] w-72 bg-white rounded-lg shadow-2xl border border-slate-200 pointer-events-none"
              style={{
                left: `${popoverPosition.x}px`,
                top: popoverPosition.showAbove ? 'auto' : `${popoverPosition.y}px`,
                bottom: popoverPosition.showAbove ? `${window.innerHeight - popoverPosition.y}px` : 'auto',
                transform: 'translateX(-50%)',
              }}
            >
              {/* Arrow */}
              <div
                className={clsx(
                  'absolute left-1/2 -translate-x-1/2 w-3 h-3 bg-white border-slate-200 rotate-45',
                  popoverPosition.showAbove
                    ? 'bottom-[-6px] border-r border-b'
                    : 'top-[-6px] border-l border-t'
                )}
              />

              <div className="p-3 relative">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-semibold text-slate-900">
                    {formatHour(hoveredHour)} - {formatHour(hoveredHour + 1)}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className={clsx(
                      'text-xs px-2 py-0.5 rounded-full font-medium',
                      getBusyLevel(hoveredHour) === 'peak' ? 'bg-danger-100 text-danger-700' :
                      getBusyLevel(hoveredHour) === 'busy' ? 'bg-warning-100 text-warning-700' :
                      getBusyLevel(hoveredHour) === 'moderate' ? 'bg-amber-100 text-amber-700' :
                      'bg-success-100 text-success-700'
                    )}>
                      {getBusyLevel(hoveredHour)}
                    </span>
                    <span className="text-xs font-medium text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full">
                      {getHourCovers(hoveredHour)} covers
                    </span>
                  </div>
                </div>

                {(reservationsByHour[hoveredHour]?.length || 0) > 0 ? (
                  <div className="space-y-2 max-h-56 overflow-y-auto">
                    {reservationsByHour[hoveredHour].map((res) => (
                      <div
                        key={res.id}
                        className={clsx(
                          'p-2.5 rounded-lg border text-sm',
                          res.guest?.isVip
                            ? 'bg-purple-50 border-purple-200'
                            : 'bg-slate-50 border-slate-200'
                        )}
                      >
                        {/* Header row */}
                        <div className="flex items-center justify-between gap-2 mb-1.5">
                          <div className="flex items-center gap-1.5 min-w-0">
                            {res.guest?.isVip && (
                              <Star className="w-3.5 h-3.5 text-purple-500 fill-purple-500 flex-shrink-0" />
                            )}
                            <span className="font-semibold text-slate-900 truncate">
                              {res.guestName}
                            </span>
                            {res.guest?.visitCount && res.guest.visitCount > 1 && (
                              <span className="text-[10px] text-slate-500 flex-shrink-0">
                                ({res.guest.visitCount} visits)
                              </span>
                            )}
                          </div>
                          <span className={clsx(
                            'text-[10px] px-1.5 py-0.5 rounded font-semibold flex-shrink-0 uppercase',
                            res.status === 'confirmed' ? 'bg-success-100 text-success-700' :
                            res.status === 'seated' ? 'bg-primary-100 text-primary-700' :
                            res.status === 'checked_in' ? 'bg-blue-100 text-blue-700' :
                            res.status === 'no_show' ? 'bg-danger-100 text-danger-700' :
                            'bg-slate-100 text-slate-600'
                          )}>
                            {res.status.replace('_', ' ')}
                          </span>
                        </div>

                        {/* Details grid */}
                        <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-slate-600">
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3 text-slate-400" />
                            <span>{formatTime(res.time)}</span>
                            {res.duration && (
                              <span className="text-slate-400">({res.duration}m)</span>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <Users className="w-3 h-3 text-slate-400" />
                            <span>{res.partySize} guest{res.partySize !== 1 ? 's' : ''}</span>
                          </div>
                          {(res.tableNumber || res.tableName) && (
                            <div className="flex items-center gap-1">
                              <MapPin className="w-3 h-3 text-slate-400" />
                              <span>Table {res.tableNumber || res.tableName}</span>
                            </div>
                          )}
                          {res.phone && (
                            <div className="flex items-center gap-1">
                              <Phone className="w-3 h-3 text-slate-400" />
                              <span className="truncate">{res.phone}</span>
                            </div>
                          )}
                        </div>

                        {/* Allergies warning */}
                        {res.guest?.allergies && res.guest.allergies.length > 0 && (
                          <div className="flex items-center gap-1.5 mt-2 px-2 py-1 rounded bg-red-50 border border-red-200">
                            <Utensils className="w-3 h-3 text-red-500 flex-shrink-0" />
                            <span className="text-xs text-red-700 font-medium">
                              Allergies: {res.guest.allergies.join(', ')}
                            </span>
                          </div>
                        )}

                        {/* Special requests */}
                        {res.specialRequests && (
                          <div className="flex items-start gap-1.5 mt-2 text-xs text-amber-700">
                            <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0 text-amber-500" />
                            <span className="line-clamp-2">{res.specialRequests}</span>
                          </div>
                        )}

                        {/* Guest tags */}
                        {res.guest?.tags && res.guest.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {res.guest.tags.slice(0, 3).map((tag, i) => (
                              <span
                                key={i}
                                className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-600"
                              >
                                {tag}
                              </span>
                            ))}
                            {res.guest.tags.length > 3 && (
                              <span className="text-[10px] text-slate-400">
                                +{res.guest.tags.length - 3} more
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-4 text-center">
                    <Users className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-400">
                      No reservations this hour
                    </p>
                  </div>
                )}

                {/* Forecast info if available */}
                {hourlyForecast.find(f => f.hour === hoveredHour) && (
                  <div className="mt-3 pt-2 border-t border-slate-200 text-xs text-slate-500">
                    <div className="flex justify-between">
                      <span>Expected walk-ins:</span>
                      <span className="font-medium text-slate-700">
                        ~{hourlyForecast.find(f => f.hour === hoveredHour)?.walkInEstimate || 0}
                      </span>
                    </div>
                    <div className="flex justify-between mt-1">
                      <span>Total expected:</span>
                      <span className="font-medium text-slate-700">
                        {hourlyForecast.find(f => f.hour === hoveredHour)?.totalExpected || 0} covers
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>,
            document.body
          )}

          {/* Current time indicator */}
          {isWithinShift && (
            <div
              className="absolute top-0 bottom-0 w-0.5 bg-primary-600 z-20"
              style={{ left: `${currentPosition}%` }}
            >
              {/* Pulse animation */}
              <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-primary-500 animate-pulse" />
              {/* Time label */}
              <div className="absolute top-full mt-1 left-1/2 -translate-x-1/2 whitespace-nowrap">
                <span className="text-[10px] font-bold text-primary-700 bg-primary-100 px-1.5 py-0.5 rounded">
                  NOW
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Legend - only show when there's data */}
        {hasData ? (
          <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-slate-500">
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-success-200 border border-success-300" />
              Low
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-amber-100 border border-amber-300" />
              Moderate
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-warning-200 border border-warning-300" />
              Busy
            </span>
            {actualPeakHour !== null && (
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-danger-200 border border-danger-300" />
                Peak
              </span>
            )}
            <span className="ml-auto text-slate-400 italic">
              Hover for details
            </span>
          </div>
        ) : (
          <p className="mt-3 text-xs text-slate-400 italic">
            No reservations or forecast data for this shift
          </p>
        )}
      </div>

      {/* Summary stats */}
      <div className="px-4 py-3 bg-slate-50 border-t border-slate-200">
        <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-sm">
          {/* Current time / Shift status */}
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-slate-400" />
            <span className="text-slate-600">
              {isBeforeShift ? (
                <>Starts in <span className="font-semibold text-primary-600">
                  {hoursUntilStart > 0 ? `${hoursUntilStart}h ` : ''}{minsUntilStart}m
                </span></>
              ) : isAfterShift || shiftStatus === 'completed' ? (
                <span className="font-semibold text-success-600">Shift Complete</span>
              ) : (
                <>
                  <span className="font-semibold text-slate-900">
                    {hoursRemaining > 0 ? `${hoursRemaining}h ` : ''}{minsRemaining}m
                  </span> remaining
                </>
              )}
            </span>
          </div>

          {/* Upcoming reservations */}
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-slate-400" />
            <span className="text-slate-600">
              <span className="font-semibold text-slate-900">{upcomingReservations.length}</span>
              {' '}reservation{upcomingReservations.length !== 1 ? 's' : ''} upcoming
            </span>
          </div>

          {/* Waitlist */}
          {waitlistCount > 0 && (
            <div className="flex items-center gap-2">
              <Timer className="w-4 h-4 text-purple-500" />
              <span className="text-slate-600">
                <span className="font-semibold text-purple-700">{waitlistCount}</span>
                {' '}on waitlist
              </span>
            </div>
          )}

          {/* Expected covers */}
          {expectedCoversRemaining > 0 && (
            <div className="flex items-center gap-2 ml-auto">
              <ChevronRight className="w-4 h-4 text-slate-400" />
              <span className="text-slate-600">
                ~<span className="font-semibold text-slate-900">{expectedCoversRemaining}</span> covers expected
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
